import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { overtimeApprovalService } from '@/api/domains/overtime/overtimeApproval.service';
import { useHoldingCompanies } from '@/api/domains/catalog/holding/hooks/useHoldingCompanies';
import {
  useEmployeeDimensionalFilter,
  type DimensionalFilterConfig,
  type UseEmployeeDimensionalFilterResult,
} from '@/hooks/attendance/common/useEmployeeDimensionalFilter';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useDebounce } from '@/hooks/ui/useDebounce';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type {
  PaginatedFetchParams,
  PaginatedFetchResult,
} from '@/hooks/ui/usePaginatedList';
import { useUI } from '@/hooks/ui/useUI';
import {
  buildOvertimeCounterDefinitions,
  EMPTY_OVERTIME_COUNTERS,
  formatOvertimeHours,
  OVERTIME_STATUS_COLORS,
  resolveOvertimeCounterKeys,
} from '@/hooks/overtime/overtimeApprovalCounters';
import type {
  OvertimeCounterDefinition,
  OvertimeHourFormat,
} from '@/hooks/overtime/overtimeApprovalCounters';
import { useOvertimeApprovalBulkActions } from '@/hooks/overtime/useOvertimeApprovalBulkActions';
import type { UseOvertimeApprovalBulkActionsResult } from '@/hooks/overtime/useOvertimeApprovalBulkActions';
import { useOvertimeApprovalMonthlyCuts } from '@/hooks/overtime/useOvertimeApprovalMonthlyCuts';
import { useOvertimeApprovalRowActions } from '@/hooks/overtime/useOvertimeApprovalRowActions';
import { useTranslation } from '@/i18n';
import { formatRut } from '@/utils';
import type { EmployeeFilteredStatus } from '@/api/domains/employee/types';
import { OVERTIME_FLOW_PROFILE_CODE } from '@/api/domains/attendance/types/attendanceConfiguration.types';
import type {
  LC_HourValueOut,
  OvertimeApprovalCounters,
  OvertimeApprovalRow,
  OvertimeCounterKey,
  OvertimeFlowProfileCode,
} from '@/api/domains/overtime/overtimeApproval.types';
import type { BannerPart } from '@/components/ui/banners';
import type { BulkActionItem, TableAction, TableColumn } from '@/components/ui/tables/Table.types';

// Re-exportados para no romper a quienes ya importan estos tipos desde este módulo
// (p. ej. `src/hooks/overtime/index.ts`) — ahora viven en `overtimeApprovalCounters`, compartidos
// con `useOvertimeApprovalDetail` (ASIS-5355).
export type { OvertimeCounterDefinition, OvertimeHourFormat };


const DEFAULT_PAGE_SIZE = 25;

/** Sentinel for the "Todos" option of the main Empresa filter. */
export const ALL_COMPANIES_VALUE = 'all';

function joinOrUndefined(ids: string[]): string | undefined {
  return ids.length > 0 ? ids.join(',') : undefined;
}

// El panel avanzado de esta pantalla usa 6 de las 7 dimensiones más la selección explícita de
// colaboradores (ASIS-5352). `location` queda fuera por decisión de negocio: la pantalla no filtra
// por ubicación. El backend valida el acceso a esos employeeIds antes de consultar.
const DIMENSIONAL_CONFIG: DimensionalFilterConfig = {
  company: true,
  area: true,
  position: true,
  costCenter: true,
  contractModality: true,
  securityGroup: true,
  employeeStatus: true,
  employee: true,
};

// Los filtros avanzados se aplican solos: se espera este lapso sin cambios antes de consultar, para
// no disparar una petición por cada opción marcada dentro de un mismo desplegable.
const ADVANCED_FILTERS_DEBOUNCE_MS = 500;

const DEFAULT_EMPLOYEE_STATUS: EmployeeFilteredStatus = 'vigente';

// Las claves de columna coinciden 1:1 con la whitelist de OvertimeApprovalOrderByFields.
const ORDER_BY_MAP: Record<string, string> = {
  rut: 'rut',
  employeeName: 'employeeName',
  calculatedHours: 'calculatedHours',
  approvedHours: 'approvedHours',
  status: 'status',
};

/**
 * Porción de los filtros que aporta el panel avanzado. Se maneja como una unidad porque se aplica
 * completa cada vez que la tabla se recarga.
 */
interface OvertimeApprovalAdvancedFilters {
  employeeIds: number[];
  companyIds: string[];
  areaIds: string[];
  positionIds: string[];
  costCenterIds: string[];
  locationIds: string[];
  contractModalityIds: string[];
  groupIds: string[];
  employeeStatus: EmployeeFilteredStatus;
}

const EMPTY_ADVANCED_FILTERS: OvertimeApprovalAdvancedFilters = {
  employeeIds: [],
  companyIds: [],
  areaIds: [],
  positionIds: [],
  costCenterIds: [],
  locationIds: [],
  contractModalityIds: [],
  groupIds: [],
  employeeStatus: DEFAULT_EMPLOYEE_STATUS,
};

interface OvertimeApprovalFilters extends OvertimeApprovalAdvancedFilters {
  monthlyCutId: number | null;
  filterCompanyId: number | null;
  counterFilter: OvertimeCounterKey;
}

/**
 * Firma canónica de un snapshot de filtros avanzados, usada para decidir si hay un cambio real que
 * amerite recargar la grilla. Se construye con los campos en orden fijo y los arrays ordenados, de
 * modo que ni el orden de declaración de las claves ni el orden en que el usuario marcó las
 * opciones alteren el resultado: seleccionar 502 y luego 501 describe el mismo universo que 501,502
 * y no debe disparar una consulta.
 */
function buildAdvancedSignature(filters: OvertimeApprovalAdvancedFilters): string {
  return JSON.stringify([
    [...filters.employeeIds].sort((a, b) => a - b),
    [...filters.companyIds].sort(),
    [...filters.areaIds].sort(),
    [...filters.positionIds].sort(),
    [...filters.costCenterIds].sort(),
    [...filters.locationIds].sort(),
    [...filters.contractModalityIds].sort(),
    [...filters.groupIds].sort(),
    filters.employeeStatus,
  ]);
}

export interface UseOvertimeApprovalResult {
  rows: OvertimeApprovalRow[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<OvertimeApprovalRow>[];
  isLoading: boolean;
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  // Main filters
  companyOptions: { value: string; label: string }[];
  selectedCompanyValue: string;
  onCompanyChange: (value: string) => void;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  descriptionOptions: { value: string; label: string }[];
  selectedYear: number | null;
  selectedMonth: number | null;
  selectedMonthlyCutId: number | null;
  onYearChange: (value: string) => void;
  onMonthChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  isLoadingCuts: boolean;
  arePeriodFiltersDisabled: boolean;
  noCutsMessage: string | null;
  // Counters + format switch
  counters: OvertimeCounterDefinition[];
  onCounterSelect: (key: OvertimeCounterKey) => void;
  hourFormat: OvertimeHourFormat;
  isDecimalFormat: boolean;
  onHourFormatToggle: (useDecimal: boolean) => void;
  // Advanced (dimensional) filters — se aplican solos, sin botón Buscar
  dimensionalConfig: DimensionalFilterConfig;
  dimensionalFilter: UseEmployeeDimensionalFilterResult;
  isAdvancedFiltersDisabled: boolean;
  isCompanyLocked: boolean;
  // Export (habilitación únicamente; la descarga en sí está fuera del alcance de ASIS-5351)
  canDownload: boolean;
  isMonthlyCutClosed: boolean;
  // Selección y acciones masivas (ASIS-5353)
  isSelectable: boolean;
  bulkActions: BulkActionItem[];
  onSelectionChange: (selected: OvertimeApprovalRow[]) => void;
  isRowSelectable: (row: OvertimeApprovalRow) => boolean;
  /** Selección vigente entregada a la tabla en modo controlado. */
  selectedRowIds: Set<string | number>;
  closedCutMessage: BannerPart[] | null;
  // Acciones unitarias por fila (ASIS-5371)
  rowActions: TableAction<OvertimeApprovalRow>[];
}

export function useOvertimeApproval(): UseOvertimeApprovalResult {
  const { t: translate } = useTranslation();
  const { showApiError, openSidePanel } = useUI();
  const { isLoaded, canView } = usePagePermissions();

  const { companies, loading: isLoadingCompanies } = useHoldingCompanies();

  const [selectedCompanyValue, setSelectedCompanyValue] = useState<string>('');
  const [counterValues, setCounterValues] = useState<OvertimeApprovalCounters>(
    EMPTY_OVERTIME_COUNTERS,
  );
  // El backend ya resuelve si el corte está cerrado. Se expone aquí a la espera del aviso visible
  // de corte cerrado, que está fuera del alcance de ASIS-5351 y llega con la entrega de aprobación.
  const [isMonthlyCutClosed, setIsMonthlyCutClosed] = useState(false);
  const [hourFormat, setHourFormat] = useState<OvertimeHourFormat>('HH:MM:SS');
  // Perfil efectivo del usuario en el flujo REVISOR -> APROBADOR (ASIS-5358). Lo resuelve el
  // backend desde el claim; `null` (flujo apagado o sin rol mapeado) equivale a la vista histórica
  // del aprobador. Nunca se asume revisor por omisión.
  const [viewerFlowProfile, setViewerFlowProfile] = useState<OvertimeFlowProfileCode | null>(null);

  const hasInitialisedCompany = useRef(false);

  // Empresa del filtro principal como CSV, para que el panel avanzado refleje siempre la misma
  // empresa que el filtro principal (bloqueando su propio selector de Empresa).
  const isCompanyLocked = Boolean(selectedCompanyValue)
    && selectedCompanyValue !== ALL_COMPANIES_VALUE;

  const lockedCompanyIds = useMemo(
    () => (isCompanyLocked ? [selectedCompanyValue] : []),
    [isCompanyLocked, selectedCompanyValue],
  );

  const dimensionalFilter = useEmployeeDimensionalFilter(
    DIMENSIONAL_CONFIG,
    DEFAULT_EMPLOYEE_STATUS,
    { lockedCompanyIds },
  );

  // Preselección inicial: la empresa padre (raíz) del holding del usuario.
  useEffect(() => {
    if (hasInitialisedCompany.current || isLoadingCompanies || companies.length === 0) return;

    const parentCompany = companies.find((company) => company.parentHoldingCompanyId === undefined)
      ?? companies[0];

    hasInitialisedCompany.current = true;
    setSelectedCompanyValue(String(parentCompany.id));
  }, [companies, isLoadingCompanies]);

  const initialFilters = useMemo<OvertimeApprovalFilters>(() => ({
    monthlyCutId: null,
    filterCompanyId: null,
    counterFilter: 'calculated',
    ...EMPTY_ADVANCED_FILTERS,
  }), []);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<OvertimeApprovalFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<OvertimeApprovalRow> | null> => {
      if (params.filters.monthlyCutId === null) return null;

      const response = await overtimeApprovalService.list(
        {
          monthlyCutId: params.filters.monthlyCutId,
          filterCompanyId: params.filters.filterCompanyId,
          counterFilter: params.filters.counterFilter,
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          orderBy: params.sort?.key,
          orderDirection: params.sort?.direction,
          employeeIds: joinOrUndefined(params.filters.employeeIds.map(String)),
          companyIds: joinOrUndefined(params.filters.companyIds),
          areaIds: joinOrUndefined(params.filters.areaIds),
          positionIds: joinOrUndefined(params.filters.positionIds),
          costCenterIds: joinOrUndefined(params.filters.costCenterIds),
          locationIds: joinOrUndefined(params.filters.locationIds),
          contractModalityIds: joinOrUndefined(params.filters.contractModalityIds),
          groupIds: joinOrUndefined(params.filters.groupIds),
          employeeStatus: params.filters.employeeStatus,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        setCounterValues(EMPTY_OVERTIME_COUNTERS);
        setViewerFlowProfile(null);
        showApiError(response, translate('attendance:overtimeApproval.errors.loadFailed'));
        return null;
      }

      setCounterValues(response.data.counters);
      setIsMonthlyCutClosed(response.data.isMonthlyCutClosed);
      setViewerFlowProfile(response.data.viewerFlowProfile);

      return {
        items: response.data.items,
        totalRecords: response.data.totalRecords,
      };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    refetch,
    debouncedSearchText,
  } = usePaginatedList<OvertimeApprovalRow, OvertimeApprovalFilters>({
    fetchFn,
    initialFilters,
    initialSort: { key: 'employeeName', direction: 'ASC' },
    initialPageSize: DEFAULT_PAGE_SIZE,
    orderByMap: ORDER_BY_MAP,
    enabled: isLoaded && canView,
  });

  // Snapshot de los filtros avanzados vigentes: se aplica completo junto con los principales cada
  // vez que la tabla se recarga.
  const advancedFilters = useMemo<OvertimeApprovalAdvancedFilters>(() => ({
    employeeIds: dimensionalFilter.selectedEmployeeIds,
    companyIds: dimensionalFilter.dimensionalFilters.companyIds,
    areaIds: dimensionalFilter.dimensionalFilters.areaIds,
    positionIds: dimensionalFilter.dimensionalFilters.positionIds,
    costCenterIds: dimensionalFilter.dimensionalFilters.costCenterIds,
    locationIds: dimensionalFilter.dimensionalFilters.locationIds,
    contractModalityIds: dimensionalFilter.dimensionalFilters.contractModalityIds,
    groupIds: dimensionalFilter.dimensionalFilters.groupIds,
    employeeStatus: dimensionalFilter.dimensionalFilters.status,
  }), [dimensionalFilter.dimensionalFilters, dimensionalFilter.selectedEmployeeIds]);

  // Espejo del snapshot para los caminos que reescriben los filtros de forma asíncrona (carga de
  // cortes), que necesitan el valor vigente sin volver a crearse en cada cambio del panel.
  const advancedFiltersRef = useRef(advancedFilters);
  useEffect(() => {
    advancedFiltersRef.current = advancedFilters;
  }, [advancedFilters]);

  // Firma del último snapshot ya aplicado a la consulta. Se compara por VALOR: los objetos y
  // arrays del panel cambian de identidad en cada render y sin esta guarda cada uno provocaría
  // una recarga de la grilla.
  const appliedAdvancedSignatureRef = useRef<string>(
    buildAdvancedSignature(EMPTY_ADVANCED_FILTERS),
  );

  // Espejo del corte vigente: el efecto del debounce necesita saber si ya hay corte sin volver a
  // suscribirse a cada cambio de `filters`.
  const monthlyCutIdRef = useRef<number | null>(null);
  useEffect(() => {
    monthlyCutIdRef.current = filters.monthlyCutId;
  }, [filters.monthlyCutId]);

  // Único punto que fusiona un parche de filtros (cambio de empresa o de período, decidido por
  // useOvertimeApprovalMonthlyCuts) con el snapshot avanzado pendiente. Marca ese snapshot como
  // aplicado para que el efecto de debounce de más abajo no lo vuelva a disparar.
  const onFiltersPatch = useCallback((patch: Partial<OvertimeApprovalFilters>) => {
    appliedAdvancedSignatureRef.current = buildAdvancedSignature(advancedFiltersRef.current);
    setFilters((prev) => ({ ...prev, ...advancedFiltersRef.current, ...patch }));
  }, [setFilters]);

  const {
    isLoadingCuts,
    hasCuts,
    yearOptions,
    monthOptions,
    descriptionOptions,
    selectedYear,
    selectedMonth,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
    noCutsMessage,
  } = useOvertimeApprovalMonthlyCuts({ selectedCompanyValue, onFiltersPatch });

  // Sin cortes disponibles el panel avanzado queda bloqueado y, si estaba abierto, se contrae.
  const {
    isExpanded: isAdvancedExpanded,
    toggleExpanded: toggleAdvancedExpanded,
  } = dimensionalFilter;

  useEffect(() => {
    if (!hasCuts && isAdvancedExpanded) {
      toggleAdvancedExpanded();
    }
  }, [hasCuts, isAdvancedExpanded, toggleAdvancedExpanded]);

  const onCompanyChange = useCallback((value: string) => {
    setSelectedCompanyValue(value);
  }, []);

  // AC2: los filtros avanzados se aplican solos. El debounce agrupa los cambios de un mismo
  // desplegable en una única consulta y la guarda por firma evita recargas sin cambio real.
  const debouncedAdvancedFilters = useDebounce(advancedFilters, ADVANCED_FILTERS_DEBOUNCE_MS);

  useEffect(() => {
    const signature = buildAdvancedSignature(debouncedAdvancedFilters);
    if (signature === appliedAdvancedSignatureRef.current) return;

    // Sin corte no hay consulta que hacer. Se sale SIN marcar la firma: el snapshot sigue
    // pendiente y lo aplicará `onFiltersPatch()` cuando llegue un corte. Marcarlo aquí lo
    // daría por aplicado y los filtros quedarían huérfanos.
    if (monthlyCutIdRef.current === null) return;

    appliedAdvancedSignatureRef.current = signature;
    setFilters((prev) => ({ ...prev, ...debouncedAdvancedFilters }));
  }, [debouncedAdvancedFilters, setFilters]);

  const onCounterSelect = useCallback((key: OvertimeCounterKey) => {
    setFilters((prev) => ({ ...prev, counterFilter: key }));
  }, [setFilters]);

  const onHourFormatToggle = useCallback((useDecimal: boolean) => {
    setHourFormat(useDecimal ? 'DECIMAL' : 'HH:MM:SS');
  }, []);

  const formatHours = useCallback(
    (value: LC_HourValueOut): string => formatOvertimeHours(value, hourFormat),
    [hourFormat],
  );

  const companyOptions = useMemo(() => [
    { value: ALL_COMPANIES_VALUE, label: translate('attendance:overtimeApproval.filters.allCompanies') },
    ...companies.map((company) => ({ value: String(company.id), label: company.name })),
  ], [companies, translate]);

  const isReviewerView = viewerFlowProfile === OVERTIME_FLOW_PROFILE_CODE.REVIEWER;

  /* Justificación del revisor (ASIS-5358): vive en el panel lateral derecho, no en un modal. Los
     colaboradores sobre los que opera viajan como prop del panel — no se guardan en este hook —
     porque la acción unitaria del menú de fila opera sobre una fila que no necesariamente está
     seleccionada, y así ambas entradas (fila y selección masiva) comparten el mismo panel.

     `justifySelection` se define más abajo (depende de `rows`), así que se alcanza por ref para
     romper el ciclo: el panel se abre antes de que exista la función, pero la invoca después. */
  const justifySelectionRef = useRef<UseOvertimeApprovalBulkActionsResult['justifySelection'] | null>(null);

  const openJustifyPanel = useCallback((
    targetRows: OvertimeApprovalRow[],
    options: { fromSelection: boolean },
  ) => {
    if (targetRows.length === 0) return;

    openSidePanel({
      component: 'OvertimeJustifyPanel',
      title: translate('attendance:overtimeApproval.bulkJustify.confirmTitle'),
      props: {
        employeeNames: targetRows.map((row) => row.employeeName),
        // El desenlace se devuelve al panel para que solo cierre en éxito: ante un error del
        // backend el motivo y el comentario ya tecleados deben sobrevivir para reintentar, igual
        // que en el detalle (`useOvertimeApprovalDetailActions.onJustify`). Un ref aún sin poblar
        // cuenta como fallo, que es lo que deja el panel abierto.
        onConfirm: async (justificationTypeId: number, freeComment: string | undefined) => (
          await justifySelectionRef.current?.(
            targetRows,
            justificationTypeId,
            freeComment,
            { fromSelection: options.fromSelection },
          ) === true
        ),
      },
    });
  }, [openSidePanel, translate]);

  // Set de contadores del perfil: el revisor ve Calculadas, Pendientes y Justificadas; cualquier
  // otro perfil (incluido el doble, que el backend resuelve como APROBADOR) ve los cuatro
  // históricos (ASIS-5358).
  const counterKeys = useMemo(
    () => resolveOvertimeCounterKeys(viewerFlowProfile),
    [viewerFlowProfile],
  );

  const counters = useMemo<OvertimeCounterDefinition[]>(() => buildOvertimeCounterDefinitions({
    translate,
    counterValues,
    activeCounterFilter: filters.counterFilter,
    hourFormat,
    counterKeys,
  }), [translate, counterValues, filters.counterFilter, hourFormat, counterKeys]);

  // Al cambiar el perfil, un filtro por contador que ya no existe en el set vigente dejaría la
  // grilla filtrada por un estado que el usuario no puede ver ni deseleccionar. Se cae a
  // "calculated", que es "sin filtro".
  useEffect(() => {
    if (!counterKeys.includes(filters.counterFilter)) {
      setFilters((prev) => ({ ...prev, counterFilter: 'calculated' }));
    }
  }, [counterKeys, filters.counterFilter, setFilters]);

  const columns: TableColumn<OvertimeApprovalRow>[] = useMemo(() => [
    {
      key: 'rut',
      header: translate('attendance:overtimeApproval.table.rut'),
      sortable: true,
      render: (row: OvertimeApprovalRow) => formatRut(row.rut),
    },
    {
      key: 'employeeName',
      header: translate('attendance:overtimeApproval.table.employee'),
      sortable: true,
    },
    {
      key: 'calculatedHours',
      header: translate('attendance:overtimeApproval.table.calculated'),
      sortable: true,
      render: (row: OvertimeApprovalRow) => formatHours(row.calculatedHours),
    },
    // Cuarta columna según la mitad del flujo de quien mira (ASIS-5358): al revisor las horas
    // aprobadas no le dicen nada — su dato son las que él marcó como justificadas.
    isReviewerView
      ? {
        key: 'reviewerJustifiedHours',
        header: translate('attendance:overtimeApproval.table.justified'),
        sortable: true,
        render: (row: OvertimeApprovalRow) => formatHours(row.reviewerJustifiedHours),
      }
      : {
        key: 'approvedHours',
        header: translate('attendance:overtimeApproval.table.approved'),
        sortable: true,
        render: (row: OvertimeApprovalRow) => formatHours(row.approvedHours),
      },
    {
      key: 'status',
      header: translate('attendance:overtimeApproval.table.status'),
      sortable: true,
      render: (row: OvertimeApprovalRow) => (
        <span style={{ color: OVERTIME_STATUS_COLORS[row.status], fontWeight: 600 }}>
          {row.status}
        </span>
      ),
    },
  ], [translate, formatHours, isReviewerView]);

  const {
    isSelectable,
    bulkActions,
    onSelectionChange,
    isRowSelectable,
    selectedRowIds,
    closedCutMessage,
    justifySelection,
  } = useOvertimeApprovalBulkActions({
    rows,
    monthlyCutId: filters.monthlyCutId,
    isMonthlyCutClosed,
    refetch,
    filtersSnapshot: filters,
    debouncedSearchText,
    isReviewerView,
    onOpenJustifyPanel: openJustifyPanel,
  });

  // Cierra el ciclo declarado arriba: desde aquí el panel ya puede ejecutar la justificación.
  justifySelectionRef.current = justifySelection;

  const { actions: rowActions } = useOvertimeApprovalRowActions({
    monthlyCutId: filters.monthlyCutId,
    isMonthlyCutClosed,
    refetch,
    isReviewerView,
    onOpenJustifyPanel: (row) => openJustifyPanel([row], { fromSelection: false }),
  });

  /* El catálogo de motivos ya no se pide acá: lo carga el propio `OvertimeJustifyPanel` al abrirse.
     `openSidePanel` congela los props en el store, así que un catálogo que llega después de abrir
     el panel no se reflejaría si viajara por prop. */

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    isLoading: loading || isLoadingCuts,
    onSearch,
    onSortChange,
    companyOptions,
    selectedCompanyValue,
    onCompanyChange,
    yearOptions,
    monthOptions,
    descriptionOptions,
    selectedYear,
    selectedMonth,
    selectedMonthlyCutId: filters.monthlyCutId,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
    isLoadingCuts,
    arePeriodFiltersDisabled: !hasCuts,
    noCutsMessage,
    counters,
    onCounterSelect,
    hourFormat,
    isDecimalFormat: hourFormat === 'DECIMAL',
    onHourFormatToggle,
    dimensionalConfig: DIMENSIONAL_CONFIG,
    dimensionalFilter,
    isAdvancedFiltersDisabled: !hasCuts,
    isCompanyLocked,
    canDownload: hasCuts && total > 0,
    isMonthlyCutClosed,
    isSelectable,
    bulkActions,
    onSelectionChange,
    isRowSelectable,
    selectedRowIds,
    closedCutMessage,
    rowActions,
  };
}

export default useOvertimeApproval;
